import { Component, signal } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter } from 'rxjs/operators';
import { PortalService } from './core/services/portal.service';
import { PortalContextService } from './core/services/portal-context.service';
import { TenantEnvService } from './core/services/tenant-env.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.html',
  standalone: false,
  styleUrl: './app.scss'
})
export class App {
  protected readonly title = signal('tenant');

  loading = true;
  logoUrl = '';
  logoWidth = '169px';
  logoHeight = '36px';
  headerLogoUrl = '';
  headerLogoWidth = '169px';
  headerLogoHeight = '36px';
  footerLogoUrl = '';
  footerLogoWidth = '169px';
  footerLogoHeight = '36px';
  nameBranch = '';
  isAdmin = false;
  portalLoaded = false;
  isPortalPublic = true;
  usesCertification = true;
  showRouteRestrictedNotFound = false;

  constructor(
    private portalService: PortalService,
    private portalContextService: PortalContextService,
    private tenantEnvService: TenantEnvService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    this.fetchPortal();
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd)
      )
      .subscribe((event) => {
        this.isAdmin = this.isAdminRoute();
        this.enforcePortalAccess(event.urlAfterRedirects);
      });
  }
  incomingUrl = '';
  tenantName = '';
  fetchPortal() {
    this.incomingUrl = this.portalContextService.getApiBaseUrl();
    this.tenantName = this.portalContextService.getTenantName();
    this.tenantEnvService.setEnvBaseUrl(this.incomingUrl);
    this.tenantEnvService.setTenantName(this.tenantName);

    this.loading = true;
    this.portalService.getPortals().subscribe({
      next: (resPortal: any) => {
        this.portalService.setPortal(resPortal);
        const portalData = resPortal;
        this.isPortalPublic = this.toBoolean(portalData?.is_public, true);
        this.usesCertification = this.toBoolean(portalData?.uses_certification, true);
        this.portalLoaded = true;
        this.enforcePortalAccess(this.router.url);
        const image = portalData?.image ?? {};
        this.logoUrl = image.logo_url ?? '';
        this.logoWidth = this.toCssSize(image?.header_footer_size?.width, '169px');
        this.logoHeight = this.toCssSize(image?.header_footer_size?.height, '36px');
        const headerFooterUrl = typeof image.header_footer_url === 'string' ? image.header_footer_url.trim() : '';
        if (headerFooterUrl) {
          this.headerLogoUrl = headerFooterUrl;
          this.footerLogoUrl = headerFooterUrl;
        }
        this.headerLogoWidth = this.toCssSize(image?.header_footer_size?.width, '169px');
        this.headerLogoHeight = this.toCssSize(image?.header_footer_size?.height, '36px');
        this.footerLogoWidth = this.toCssSize(image?.header_footer_size?.width, '169px');
        this.footerLogoHeight = this.toCssSize(image?.header_footer_size?.height, '36px');
        this.nameBranch = portalData.name;
        this.loading = false;
      },
      error: () => {
        this.portalLoaded = true;
        this.loading = false;
      },
    });
  }

  private isAdminRoute(): boolean {
    let route = this.activatedRoute;
    while (route.firstChild) {
      if (route.snapshot.data['isAdmin'] === true) {
        return true;
      }
      route = route.firstChild;
    }
    return route.snapshot.data['isAdmin'] === true;
  }

  private enforcePortalAccess(url: string): void {
    if (!this.portalLoaded) return;

    if (!this.isPortalPublic) {
      this.showRouteRestrictedNotFound = false;
      return;
    }

    if (this.usesCertification) {
      this.showRouteRestrictedNotFound = false;
      return;
    }

    this.showRouteRestrictedNotFound = !this.isRouteAllowedWithoutCertification(url);
  }

  private isRouteAllowedWithoutCertification(url: string): boolean {
    const segments = this.getPrimarySegments(url);
    if (segments.length === 0) return true;
    return segments.length === 2 && segments[0] === 'product' && !!segments[1];
  }

  private getPrimarySegments(url: string): string[] {
    const tree = this.router.parseUrl(url || '/');
    const primary = tree.root.children['primary'];
    return primary?.segments.map((segment) => segment.path) ?? [];
  }

  private toBoolean(value: unknown, fallback: boolean): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true') return true;
      if (normalized === 'false') return false;
    }
    return fallback;
  }

  private toCssSize(value: unknown, fallback: string): string {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return `${value}px`;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return fallback;
      if (/^\d+(\.\d+)?$/.test(trimmed)) return `${trimmed}px`;
      return trimmed;
    }

    return fallback;
  }
}
